import { Shimmer } from '@/components/ai-elements/shimmer'
import { iconBtn } from '@/components/record-sheet/styles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  type ChatRoomDetail,
  type TeamAgent,
  addMember,
  kickMember
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { ToolLogo } from '@/lib/tool-icons'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  Check,
  Loader2,
  Plus,
  ShieldCheck,
  UserMinus,
  X
} from 'lucide-react'
import { useState } from 'react'

type Usage = ChatRoomDetail['map']['agents'][number]
type Member = ChatRoomDetail['members'][number]

interface TreeNode {
  name: string
  asked: number // times the parent messaged or asked this node
  children: TreeNode[]
}

/** You → whoever you messaged → whoever they asked. Agents only seen in usage hang off You. */
function buildTree(room: ChatRoomDetail): TreeNode {
  const root: TreeNode = { name: 'You', asked: 0, children: [] }
  const seen = new Set(['You'])
  const grow = (node: TreeNode) => {
    for (const e of room.map.edges.filter((x) => x.from === node.name)) {
      if (seen.has(e.to)) continue
      seen.add(e.to)
      const child: TreeNode = { name: e.to, asked: e.count, children: [] }
      node.children.push(child)
      grow(child)
    }
  }
  grow(root)
  for (const a of room.map.agents) {
    if (!seen.has(a.name))
      root.children.push({ name: a.name, asked: 0, children: [] })
  }
  return root
}

const compact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(1)}k`
      : String(n)

function ago(iso: string) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return new Date(iso).toLocaleDateString()
}

const lowerFirst = (s: string) =>
  s ? s.charAt(0).toLowerCase() + s.slice(1) : s

export function ChatSidePanel({
  room,
  agents,
  onClose
}: {
  room: ChatRoomDetail
  agents: TeamAgent[]
  onClose: () => void
}) {
  const [allActivity, setAllActivity] = useState(false)
  const isGroup = room.kind === 'group'
  const usage = new Map(room.map.agents.map((a) => [a.name, a]))
  const members = new Map(room.members.map((m) => [m.name, m]))
  const maxCost = Math.max(0, ...room.map.agents.map((a) => a.cost))
  const activeKeys = new Set(
    room.members.filter((m) => m.active).map((m) => m.key)
  )
  const addable = isGroup ? agents.filter((a) => !activeKeys.has(a.key)) : []
  const tree = buildTree(room)
  const activity = allActivity ? room.activity : room.activity.slice(0, 8)

  async function change(fn: () => Promise<unknown>, what: string) {
    try {
      await fn()
      await queryClient.invalidateQueries({ queryKey: ['ai-room', room.id] })
    } catch (err) {
      notifyError(what, err)
    }
  }

  const kick = (name: string) => {
    const m = members.get(name)
    if (m)
      change(() => kickMember(room.id, m.key), 'Could not remove the agent')
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between mb-3 border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
        <p className="text-sm font-semibold text-neutral-900 dark:text-white">
          Details
        </p>
        <button
          type="button"
          className={iconBtn}
          onClick={onClose}
          aria-label="Hide details"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        <section className="rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
          <p className="text-3xl font-bold tabular-nums tracking-tight text-neutral-900 dark:text-white">
            ${room.map.total.cost.toFixed(3)}
          </p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            AI cost · {room.stats.runs} runs · {room.stats.tool_calls} tool
            calls · {compact(room.map.total.tokens)} tokens
          </p>
          {room.stats.waiting > 0 && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
              <AlertCircle className="size-3.5" />
              {room.stats.waiting} waiting for you
            </p>
          )}
        </section>

        <section className="rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
              Who's working
            </h3>
            {addable.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-neutral-600 hover:bg-white hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white"
                  >
                    <Plus className="size-3.5" /> Add agent
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="max-h-[50vh] w-56 overflow-auto rounded-[36px]"
                >
                  <DropdownMenuLabel>Add to this chat</DropdownMenuLabel>
                  {addable.map((a) => (
                    <DropdownMenuItem
                      key={a.key}
                      onSelect={() =>
                        change(
                          () => addMember(room.id, a.key),
                          'Could not add the agent'
                        )
                      }
                    >
                      {a.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="text-sm">
            <p className="flex items-center gap-2 py-1 font-semibold text-neutral-900 dark:text-white">
              <span className="size-2 rounded-full bg-neutral-900 dark:bg-white" />{' '}
              You
            </p>
            {tree.children.length === 0 ? (
              <p className="pl-4 text-xs text-neutral-500 dark:text-neutral-400">
                Send a message to get started.
              </p>
            ) : (
              <Branch
                nodes={tree.children}
                parent="You"
                usage={usage}
                members={members}
                maxCost={maxCost}
                canKick={isGroup}
                onKick={kick}
              />
            )}
          </div>
          {room.kind === 'team' && (
            <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
              Everyone else is on standby. Type @ to ask someone directly.
            </p>
          )}
        </section>

        <section className="rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
          <h3 className="mb-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            Activity
          </h3>
          {room.activity.length === 0 ? (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              What the agents do shows up here live.
            </p>
          ) : (
            <ol className="space-y-3">
              {activity.map((a) => (
                <li
                  key={a.id}
                  className="flex gap-2.5 text-xs animate-in fade-in-0"
                >
                  <StatusIcon status={a.status} />
                  <div className="min-w-0 flex-1">
                    {a.status === 'running' ? (
                      <Shimmer>{`${a.agent} · ${a.label}…`}</Shimmer>
                    ) : (
                      <p className="text-neutral-700 dark:text-neutral-200">
                        {a.toolkit && (
                          <ToolLogo
                            toolkit={a.toolkit}
                            size="xs"
                            className="mr-1 inline-flex align-middle"
                          />
                        )}
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {a.agent}
                        </span>{' '}
                        {lowerFirst(a.label)}
                      </p>
                    )}
                    {a.summary && a.status !== 'running' && (
                      <p className="truncate text-neutral-500 dark:text-neutral-400">
                        {a.summary}
                      </p>
                    )}
                    {a.status === 'waiting' && (
                      <p className="font-medium text-amber-600 dark:text-amber-400">
                        Waiting for your yes
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 tabular-nums text-neutral-400">
                    {ago(a.at)}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {room.activity.length > 8 && (
            <button
              type="button"
              onClick={() => setAllActivity((v) => !v)}
              className="mt-3 text-xs font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white"
            >
              {allActivity ? 'Show less' : `Show all ${room.activity.length}`}
            </button>
          )}
        </section>
      </div>
    </div>
  )
}

function Branch({
  nodes,
  parent,
  usage,
  members,
  maxCost,
  canKick,
  onKick
}: {
  nodes: TreeNode[]
  parent: string
  usage: Map<string, Usage>
  members: Map<string, Member>
  maxCost: number
  canKick: boolean
  onKick: (name: string) => void
}) {
  return (
    <ul className="ml-[3px] border-l border-neutral-300 dark:border-neutral-700">
      {nodes.map((n) => {
        const u = usage.get(n.name)
        const m = members.get(n.name)
        const working = Boolean(m?.working)
        return (
          <li key={n.name} className="relative pl-4">
            <span className="absolute left-0 top-[15px] h-px w-3 bg-neutral-300 dark:bg-neutral-700" />
            <div className="group py-1.5 pr-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'size-2 shrink-0 rounded-full',
                    working
                      ? 'bg-emerald-500 motion-safe:animate-pulse'
                      : 'bg-neutral-300 dark:bg-neutral-600'
                  )}
                />
                <span className="min-w-0 flex-1 truncate font-medium text-neutral-900 dark:text-white">
                  {n.name}
                </span>
                {u && (
                  <span className="shrink-0 text-xs font-semibold tabular-nums text-neutral-900 dark:text-white">
                    ${u.cost.toFixed(3)}
                  </span>
                )}
                {canKick && m && !m.lead && m.active && (
                  <button
                    type="button"
                    onClick={() => onKick(n.name)}
                    className="hidden size-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-red-50 hover:text-red-600 group-hover:flex dark:hover:bg-red-500/10"
                    aria-label={`Remove ${n.name} from this chat`}
                    title="Remove from chat"
                  >
                    <UserMinus className="size-3.5" />
                  </button>
                )}
              </div>
              <p className="mt-0.5 pl-4 text-xs text-neutral-500 dark:text-neutral-400">
                {working ? (
                  <span className="text-emerald-600 dark:text-emerald-400">
                    Working…
                  </span>
                ) : n.asked ? (
                  `${parent === 'You' ? 'You messaged' : `Asked by ${parent}`} ${n.asked}×`
                ) : m?.active === false ? (
                  'Removed'
                ) : (
                  'Idle'
                )}
                {u &&
                  ` · ${u.runs} ${u.runs === 1 ? 'run' : 'runs'} · ${compact(u.tokens_in + u.tokens_out)} tokens`}
              </p>
              {u && maxCost > 0 && (
                <span className="ml-4 mt-1.5 block h-1 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                  <span
                    className="block h-full rounded-full bg-neutral-900 dark:bg-white"
                    style={{
                      width: `${Math.max(3, (u.cost / maxCost) * 100)}%`
                    }}
                  />
                </span>
              )}
            </div>
            {n.children.length > 0 && (
              <Branch
                nodes={n.children}
                parent={n.name}
                usage={usage}
                members={members}
                maxCost={maxCost}
                canKick={canKick}
                onKick={onKick}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}

function StatusIcon({ status }: { status: string }) {
  const base = 'mt-0.5 size-3.5 shrink-0'
  if (status === 'running')
    return <Loader2 className={cn(base, 'animate-spin text-neutral-500')} />
  if (status === 'error') return <X className={cn(base, 'text-red-500')} />
  if (status === 'waiting')
    return <ShieldCheck className={cn(base, 'text-amber-500')} />
  return <Check className={cn(base, 'text-emerald-500')} />
}
